<template>
  <div class="ad-page">
    <section class="hero">
      <div class="container hero-grid">
        <div>
          <p class="eyebrow">Advertising</p>
          <h1>{{ texts.hero.title }}</h1>
          <p class="lead">{{ texts.hero.desc }}</p>
          <a class="btn" :href="trialHref">{{ texts.tryFree }}</a>
        </div>
        <img class="hero-image" :src="heroImage" :alt="texts.hero.alt" />
      </div>
    </section>

    <section class="content">
      <div class="container">
        <div class="section-header">
          <h2>{{ texts.coreTitle }}</h2>
          <p>{{ texts.coreDesc }}</p>
        </div>
        <div class="feature-grid">
          <article v-for="item in texts.coreItems" :key="item.title" class="feature-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>

        <div v-for="item in texts.modules" :key="item.title" class="module-card">
          <div class="module-grid">
            <div>
              <h3>{{ item.title }}</h3>
              <p class="module-desc">{{ item.desc }}</p>
              <ul>
                <li v-for="point in item.points" :key="point.title">
                  <strong>{{ point.title }}</strong>
                  <span>{{ point.desc }}</span>
                </li>
              </ul>
              <a class="btn btn-secondary" :href="trialHref">{{ texts.tryFree }}</a>
            </div>
            <img :src="item.image" :alt="item.title" class="module-image" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { localeState } from "@/utils/locale";
import heroImage from "@/assets/92ccb6f3a8b841bac2addd83d91cd916.png";
import dashboardImage from "@/assets/64145334fb27a54d9700202a7298f18d.png";
import analysisImage from "@/assets/c3ecafb679cbac3fc72614f2a4e413a6.png";
import keywordImage from "@/assets/86ff75ea7e280b38143e6270c46241e5-1.png";
import budgetImage from "@/assets/be3ecc96878eed3223a7954ab84c4737.png";

export default {
  name: "AdPage",
  data() {
    return {
      heroImage,
      copy: {
        zh: {
          hero: {
            title: "高效益管理亚马逊广告",
            desc: "广告效果有迹可循，让每一笔广告费都花在刀刃上。",
            alt: "亚马逊广告管理",
          },
          tryFree: "免费试用",
          coreTitle: "亚马逊广告精细化智能管理",
          coreDesc: "通过统一管理、小时级数据、自动化策略和多维分析，帮助团队提升广告投放效率与回报。",
          coreItems: [
            { title: "一站式管理广告", desc: "统一管理 SP、SB、SD 等广告类型，减少多后台切换。" },
            { title: "小时级别数据", desc: "提供更细粒度的广告数据，帮助团队更快响应变化。" },
            { title: "自动规则策略", desc: "按预算、竞价、状态和广告位执行自动化调整。" },
            { title: "全局效果概览", desc: "从投放、转化到预算利用率形成统一视图。" },
          ],
          modules: [
            {
              title: "多维度数据，全局概览效果",
              desc: "自动形成多维广告可视化报表，帮助卖家快速识别广告投入与转化表现。",
              image: dashboardImage,
              points: [
                { title: "16+ 仪表盘", desc: "集中查看广告投入、转化与结构分布。" },
                { title: "店铺维度分析", desc: "支持按店铺、广告位、时间段等方式查看趋势。" },
                { title: "自动回溯报告", desc: "按需配置关注报告和数据获取频率。" },
              ],
            },
            {
              title: "深度经营分析，助力决策",
              desc: "多维数据联动分析，帮助团队找到广告表现波动的关键原因。",
              image: analysisImage,
              points: [
                { title: "透视广告效果", desc: "从 ASIN、关键词和搜索词多角度分析广告表现。" },
                { title: "Other ASIN 报表", desc: "识别促进成交的关键词和广告活动。" },
                { title: "日志溯源分析", desc: "同步操作日志，让预算变动和效果变化可追踪。" },
              ],
            },
            {
              title: "构建优质关键词矩阵",
              desc: "快速识别优质出单词和高潜力词，帮助团队提升流量质量。",
              image: keywordImage,
              points: [
                { title: "重复关键词表", desc: "发现同一关键词在不同广告组下的差异表现。" },
                { title: "出单词库分析", desc: "自动归纳并比较用户搜索词的实际转化效果。" },
                { title: "ASIN 反查", desc: "查询同类目优质流量词，辅助主打关键词筛选。" },
              ],
            },
            {
              title: "广告预算精细管理",
              desc: "从预算建议、预算调整到预算告警，帮助团队更科学地控制投放节奏。",
              image: budgetImage,
              points: [
                { title: "建议预算", desc: "结合销售额与 ACOS 预估预算合理性。" },
                { title: "递增预算", desc: "按规则自动追踪表现并调整预算。" },
                { title: "预算分析与告警", desc: "快速发现超预算时段并进行优化。" },
              ],
            },
          ],
          trialSubject: "广告功能试用咨询",
        },
        en: {
          hero: {
            title: "Manage Amazon Advertising for Better Returns",
            desc: "Make every advertising dollar traceable and easier to optimize.",
            alt: "Amazon advertising management",
          },
          tryFree: "Free Trial",
          coreTitle: "Refined Amazon Advertising Management",
          coreDesc: "Help teams improve campaign efficiency and returns through centralized control, hourly data, automation, and multi-dimensional analysis.",
          coreItems: [
            { title: "Centralized Ad Management", desc: "Manage SP, SB, SD, and other ad types in one place with fewer context switches." },
            { title: "Hourly-Level Data", desc: "Access finer-grained ad data so teams can respond faster to changes." },
            { title: "Automation Rules", desc: "Automatically adjust budgets, bids, statuses, and placements based on rules." },
            { title: "Global Performance View", desc: "Unify spend, conversion, and budget utilization into one operating view." },
          ],
          modules: [
            {
              title: "Multi-Dimensional Dashboards",
              desc: "Generate visual ad reports automatically so sellers can quickly understand spend and conversion performance.",
              image: dashboardImage,
              points: [
                { title: "16+ dashboards", desc: "Review ad spend, conversions, and structural distribution in one place." },
                { title: "Store-level analysis", desc: "Track trends by store, placement, and time range." },
                { title: "Automatic report history", desc: "Configure the reports you care about and how often to refresh them." },
              ],
            },
            {
              title: "Deeper Business Analysis",
              desc: "Connect multiple data dimensions to identify the root causes behind ad performance changes.",
              image: analysisImage,
              points: [
                { title: "Ad performance drill-down", desc: "Analyze results across ASINs, keywords, and search terms." },
                { title: "Other ASIN reports", desc: "Identify which keywords and campaigns are driving conversions." },
                { title: "Operation log tracing", desc: "Sync action logs so budget and performance changes are easier to explain." },
              ],
            },
            {
              title: "High-Quality Keyword Matrix",
              desc: "Identify strong converting keywords and high-potential terms faster to improve traffic quality.",
              image: keywordImage,
              points: [
                { title: "Duplicate keyword view", desc: "Compare how the same keyword performs across campaigns and match types." },
                { title: "Converting term analysis", desc: "Automatically summarize and compare real search term performance." },
                { title: "ASIN reverse lookup", desc: "Discover strong traffic terms in the same category to support targeting strategy." },
              ],
            },
            {
              title: "Granular Budget Management",
              desc: "From budget recommendations to adjustments and alerts, manage spend with more discipline.",
              image: budgetImage,
              points: [
                { title: "Budget suggestions", desc: "Estimate reasonableness using expected sales and ACOS." },
                { title: "Incremental budgets", desc: "Track results by rule and increase budgets automatically when needed." },
                { title: "Budget analysis and alerts", desc: "Spot over-budget periods quickly and keep optimizing." },
              ],
            },
          ],
          trialSubject: "Advertising feature trial inquiry",
        },
      },
    };
  },
  computed: {
    locale() {
      return localeState.locale;
    },
    texts() {
      return this.copy[this.locale] || this.copy.zh;
    },
    trialHref() {
      return `mailto:17835276624@163.com?subject=${encodeURIComponent(this.texts.trialSubject)}`;
    },
  },
};
</script>

<style scoped>
.ad-page {
  background: #f5f8ff;
  min-height: 100%;
  padding-bottom: 4rem;
}
.hero {
  background: #fff;
  padding: 3rem 1.25rem 2rem;
}
.container {
  margin: 0 auto;
  max-width: 80rem;
}
.hero-grid,
.module-grid {
  display: grid;
  gap: 1.5rem;
  align-items: center;
}
.eyebrow {
  font-size: 0.875rem;
  letter-spacing: 0.12em;
  margin-bottom: 0.75rem;
  color: #2554fe;
  text-transform: uppercase;
}
h1 {
  color: #1f2a44;
  font-size: 2.4rem;
  line-height: 1.25;
  margin: 0;
}
.lead,
.section-header p,
.module-desc,
.feature-card p,
li span {
  color: #667085;
  line-height: 1.85;
}
.lead {
  font-size: 1.05rem;
  margin: 1rem 0 1.5rem;
  max-width: 34rem;
}
.btn {
  display: inline-block;
  background: #2554fe;
  border-radius: 999px;
  color: #fff;
  padding: 0.8rem 1.4rem;
  text-decoration: none;
}
.btn-secondary {
  margin-top: 1rem;
}
.hero-image,
.module-image {
  width: 100%;
}
.content {
  padding: 0 1.25rem;
}
.section-header {
  padding: 3rem 0 1.5rem;
  text-align: center;
}
.section-header h2 {
  color: #173169;
  font-size: 2rem;
  margin: 0 0 0.75rem;
}
.feature-grid {
  display: grid;
  gap: 1rem;
}
.feature-card,
.module-card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 18px 48px rgba(28, 65, 148, 0.12);
}
.feature-card {
  padding: 1.25rem;
}
.feature-card h3,
.module-card h3 {
  color: #173169;
  margin: 0 0 0.5rem;
}
.module-card {
  margin-top: 1.25rem;
  padding: 1.5rem;
}
ul {
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
}
li {
  display: grid;
  gap: 0.25rem;
  margin-bottom: 0.9rem;
}
li strong {
  color: #173169;
}
@media (min-width: 1024px) {
  .hero {
    padding: 4rem 2rem 2.5rem;
  }
  .hero-grid {
    grid-template-columns: minmax(0, 1fr) minmax(420px, 0.95fr);
  }
  .feature-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .module-grid {
    grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  }
  .module-card {
    padding: 2rem;
  }
}
</style>
